import { useState, useEffect } from 'react'
import { OrderAPI } from '@certi/shared/api/order/OrderAPI'
import type { UserOrderItemResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'
import { Link, useNavigate } from 'react-router-dom'

export default function OrdersSection() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'unpaid' | 'paid'>('all')
  const [allOrders, setAllOrders] = useState<UserOrderItemResponse[]>([])
  const [unpaidOrders, setUnpaidOrders] = useState<UserOrderItemResponse[]>([])
  const [paidOrders, setPaidOrders] = useState<UserOrderItemResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!session?.accessToken) return

    const fetchOrders = async () => {
      try {
        setLoading(true)
        setError(null)

        if (session.accessToken === 'fake-access-token') {
          // Mock data for dev login
          setAllOrders([])
          setUnpaidOrders([])
          setPaidOrders([])
        } else {
          const [all, unpaid, paid] = await Promise.all([
            OrderAPI.getUserAllOrders(session.accessToken),
            OrderAPI.getUserUnpaidOrders(session.accessToken),
            OrderAPI.getUserPaidOrders(session.accessToken)
          ])
          setAllOrders(all)
          setUnpaidOrders(unpaid)
          setPaidOrders(paid)
        }
      } catch (err) {
        console.error('Failed to fetch orders:', err)
        setError(err instanceof Error ? err.message : 'Failed to load orders')
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [session?.accessToken])

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date)
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-600 text-white',
      FAILED: 'bg-red-100 text-red-800',
      REFUNDED: 'bg-gray-100 text-gray-800',
      DISPUTED: 'bg-orange-100 text-orange-800',
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      PENDING: 'Payment Needed',
      PROCESSING: 'Processing',
      COMPLETED: 'Paid',
      FAILED: 'Payment Failed',
      REFUNDED: 'Refunded',
      DISPUTED: 'Disputed',
    }
    return labels[status as keyof typeof labels] || status
  }

  const canPayForOrder = (order: UserOrderItemResponse) =>
    !order.isPaid && (order.status === 'PENDING' || order.status === 'PROCESSING')

  const shippingLabel = (order: UserOrderItemResponse) =>
    [order.shippingProvider, order.shippingService].filter(Boolean).join(' ')

  const handlePayNow = (paymentId: string) => {
    if (!session?.accessToken) {
      navigate('/login')
      return
    }

    navigate(`/checkout/${paymentId}`)
  }

  const renderOrderItem = (order: UserOrderItemResponse) => (
    <div
      key={order.paymentId}
      className="bg-white border border-gray-200 rounded-lg p-4"
    >
      <div className="flex gap-4">
        {order.listingImageUrl ? (
          <Link to={`/marketplace/${order.listingSlug}`} className="shrink-0">
            <img
              src={order.listingImageUrl}
              alt={order.listingTitle}
              className="w-24 h-24 object-cover rounded"
            />
          </Link>
        ) : null}
        <div className="flex-1">
          <Link
            to={`/marketplace/${order.listingSlug}`}
            className="font-semibold text-gray-900 mb-1 inline-block hover:text-brand-primary transition-colors"
          >
            {order.listingTitle}
          </Link>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total:</span>
              <span className="font-medium">{formatPrice(order.totalAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created:</span>
              <span className="text-gray-900">{formatDate(order.createdAt)}</span>
            </div>
            {order.paidAt && (
              <div className="flex justify-between">
                <span className="text-gray-600">Paid:</span>
                <span className="text-gray-900">{formatDate(order.paidAt)}</span>
              </div>
            )}
          </div>

          {order.isPaid && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">Shipping</p>
                  <p className="text-gray-600">
                    {shippingLabel(order) || 'Preparing shipment'}
                    {order.shippingEstimatedDays ? `, estimated ${order.shippingEstimatedDays} days` : ''}
                  </p>
                </div>
                {isShippingLabelFailed(order) ? (
                  <span className="shrink-0 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                    Needs review
                  </span>
                ) : order.shippingTrackingNumber ? (
                  <span className="shrink-0 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                    Tracking ready
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-semibold text-yellow-800">
                    Preparing
                  </span>
                )}
              </div>

              {order.shippingTrackingNumber ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3">
                  <span className="font-mono text-xs text-gray-900">{order.shippingTrackingNumber}</span>
                  {order.shippingTrackingUrl ? (
                    <a
                      href={order.shippingTrackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-brand-primary hover:underline"
                    >
                      Track package
                    </a>
                  ) : null}
                </div>
              ) : isShippingLabelFailed(order) ? (
                <div className="mt-3 border-t border-red-100 pt-3">
                  <p className="text-xs font-medium text-red-700">
                    The carrier label was not created automatically.
                  </p>
                  {order.shippingLabelError ? (
                    <p className="mt-1 text-xs text-red-600">{order.shippingLabelError}</p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-gray-600">
                  Tracking will appear here after the shipping label is created.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-3">
        <Link
          to={`/marketplace/${order.listingSlug}`}
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          View item
        </Link>
        {canPayForOrder(order) ? (
          <button
            type="button"
            onClick={() => handlePayNow(order.paymentId)}
            className="btn btn-primary"
          >
            Pay now
          </button>
        ) : null}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">My Orders</h2>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'all'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('unpaid')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'unpaid'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Unpaid
          </button>
          <button
            onClick={() => setActiveTab('paid')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'paid'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Paid
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'all' && (
          <div>
            {allOrders.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any orders at the moment.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {allOrders.map(renderOrderItem)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'unpaid' && (
          <div>
            {unpaidOrders.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No unpaid orders</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any unpaid orders at the moment.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {unpaidOrders.map(renderOrderItem)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'paid' && (
          <div>
            {paidOrders.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No paid orders</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  You don't have any paid orders at the moment.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {paidOrders.map(renderOrderItem)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function isShippingLabelFailed(order: UserOrderItemResponse) {
  const status = order.shippingLabelStatus?.toUpperCase()
  return status === 'ERROR' || status === 'FAILURE'
}
